import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { BreadcrumbNavigationService, Debounce, SubscriptionCollector, TrackingV2Service } from '@draeger-academy/common';
import { ResponsiveState } from '@viablelogic/ngx-responsive';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

interface BreadcrumbLinksInterface {
    link: string;
    label: string;
    showText: boolean;
    forceLink?: boolean;
}

const LEADING_ITEMS_COUNT = 2;

@Component({
    selector: 'da-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
    public selectedBusinessField: string;
    public truncateAfter: number;
    @ViewChild('breadcrumbContainer') breadcrumbContainer: ElementRef;
    @ViewChild('breadcrumb') breadcrumb: ElementRef;
    private _initialBreadcrumbSize = 0;
    @SubscriptionCollector() private _subscriptions = [];
    private _isInititalCall = true;
    private _$isMobile: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$breadcrumbContainerWidth: BehaviorSubject<number> = new BehaviorSubject(0);
    private _$breadcrumbWidth: BehaviorSubject<number> = new BehaviorSubject(0);
    private _$isCollapsed: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$needsTruncate: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showBreadcrumb: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$breadcrumbItems: BehaviorSubject<BreadcrumbLinksInterface[]> = new BehaviorSubject([]);
    public middleScreen: boolean;
    public bigScreen: boolean;
    public shortScreen: boolean;

    constructor(
        private _breadcrumbService: BreadcrumbNavigationService,
        public responsiveState: ResponsiveState,
        protected _ref: ChangeDetectorRef,
        private _trackingV2Service: TrackingV2Service
    ) {
        this.truncateAfter = LEADING_ITEMS_COUNT;
        this._showBreadcrumb();
    }

    public get breadcrumbItems$(): Observable<BreadcrumbLinksInterface[]> {
        return this._$breadcrumbItems.asObservable();
    }

    public get showBreadcrumb$(): Observable<boolean> {
        return this._$showBreadcrumb.asObservable();
    }

    public get isMobile$(): Observable<boolean> {
        return this._$isMobile.asObservable();
    }

    public get breadcrumbContainerWidth$(): number {
        return this._$breadcrumbContainerWidth.getValue();
    }

    public get breadcrumbWidth$(): number {
        return this._$breadcrumbWidth.getValue();
    }

    public get getCollapsedStatusClass(): string {
        const isExpanded = this._$isCollapsed.getValue();
        return isExpanded ? 'collapsed' : 'expanded';
    }

    public get needsTruncate(): boolean {
        return this._$needsTruncate.getValue();
    }

    @HostListener('window:resize')
    resizeBy() {
        this._resizeHandler();
    }

    ngOnInit() {
        this.screenWidth();
        this._breadcrumbService.initBreadcrumb();
        this._breadcrumbService.breadcrumb$.subscribe((data) => {
            if (data) {
                this._$breadcrumbItems.next(data);
                this._resizeHandler();
                if (data) {
                    const simpleBreadcrumb = data.map((entry) => {
                        return entry.label;
                    });
                    this._trackingV2Service.setBreadcrumb(simpleBreadcrumb);
                }
            }
        });
    }

    ngOnDestroy() {
        this._subscriptions.forEach((data) => {
            if (data) {
                data.unsubscribe();
            }
        });
    }

    private _updateBreadcrumb(): any {
        if (this.breadcrumb && this.breadcrumbContainer) {
            this._$breadcrumbWidth.next(this.breadcrumb.nativeElement.clientWidth);
            this._$breadcrumbContainerWidth.next(this.breadcrumbContainer.nativeElement.clientWidth);
            this._setNeedsTruncate();
            this._$isCollapsed.next(this._$needsTruncate.getValue());
        }
    }

    @Debounce(500)
    updateExpandStatus(collapseBreadcrumb: boolean) {
        if (this.needsTruncate) {
            if (collapseBreadcrumb) {
                this._$isCollapsed.next(true);
            } else {
                this._$isCollapsed.next(false);
            }
        }
    }

    @Debounce(200)
    private _resizeHandler() {
        this.responsiveState.elemento$.pipe(distinctUntilChanged()).subscribe((val) => {
            const _showMobile = val === 'xs' || val === 'sm';
            this._$isMobile.next(_showMobile);
            if (this._$breadcrumbItems.getValue() && !_showMobile) {
                this._updateBreadcrumb();
            }
        });
        this.screenWidth();
    }

    private _setNeedsTruncate() {
        if (this._isInititalCall) {
            this._$needsTruncate.next(this.breadcrumbWidth$ > (Number(this.breadcrumbContainerWidth$) * parseFloat('75')) / 100);
            this._initialBreadcrumbSize = this.breadcrumbWidth$;
            this._isInititalCall = false;
        } else {
            this._$needsTruncate.next(this._initialBreadcrumbSize > (Number(this.breadcrumbContainerWidth$) * parseFloat('75')) / 100);
        }
    }

    private _showBreadcrumb() {
        this.responsiveState.elemento$.pipe(distinctUntilChanged()).subscribe((val) => {
            this._$showBreadcrumb.next(val !== 'xs' && val !== 'sm');
        });
    }

    private screenWidth() {
        this.bigScreen = false;
        this.middleScreen = false;
        this.shortScreen = false;

        if (window.innerWidth >= 1200) {
            this.bigScreen = true;
        }
        if (window.innerWidth < 1200 && window.innerWidth >= 900) {
            this.middleScreen = true;
        }
        if (window.innerWidth < 900) {
            this.shortScreen = true;
        }
        
    }
}
